import { useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';

export default function AccountPage() {
  const { user, refreshUser } = useAuth();
  const { t } = useLang();

  // Wachtwoord wijzigen
  const [pwForm, setPwForm]   = useState({ current: '', next: '', confirm: '' });
  const [pwMsg, setPwMsg]     = useState(null);
  const [pwLoading, setPwLoading] = useState(false);

  // MFA
  const [setup, setSetup]       = useState(null); // { qr, secret }
  const [mfaCode, setMfaCode]   = useState('');
  const [mfaMsg, setMfaMsg]     = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [disabling, setDisabling]   = useState(false);
  const [disableCode, setDisableCode] = useState('');

  async function changePassword(e) {
    e.preventDefault();
    setPwMsg(null);
    if (pwForm.next.length < 8)        { setPwMsg({ type: 'danger', text: t('reset_too_short') }); return; }
    if (pwForm.next !== pwForm.confirm) { setPwMsg({ type: 'danger', text: t('reset_mismatch') }); return; }
    setPwLoading(true);
    try {
      await api.put('/api/users/me/password', { current_password: pwForm.current, new_password: pwForm.next });
      setPwMsg({ type: 'success', text: t('account_pw_changed') });
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPwMsg({ type: 'danger', text: err.response?.data?.error || t('error') });
    } finally { setPwLoading(false); }
  }

  async function startMfa() {
    setMfaMsg(null); setMfaLoading(true);
    try {
      const res = await api.post('/api/auth/mfa/setup');
      setSetup(res.data); setMfaCode('');
    } catch (err) { setMfaMsg({ type: 'danger', text: err.response?.data?.error || t('error') }); }
    finally { setMfaLoading(false); }
  }

  async function confirmMfa(e) {
    e.preventDefault();
    setMfaMsg(null); setMfaLoading(true);
    try {
      await api.post('/api/auth/mfa/enable', { code: mfaCode });
      setSetup(null); setMfaCode('');
      setMfaMsg({ type: 'success', text: t('account_mfa_enabled') });
      await refreshUser();
    } catch (err) { setMfaMsg({ type: 'danger', text: err.response?.data?.error || t('error') }); }
    finally { setMfaLoading(false); }
  }

  async function disableMfa(e) {
    e.preventDefault();
    setMfaMsg(null); setMfaLoading(true);
    try {
      await api.post('/api/auth/mfa/disable', { code: disableCode });
      setDisabling(false); setDisableCode('');
      setMfaMsg({ type: 'success', text: t('account_mfa_disabled') });
      await refreshUser();
    } catch (err) { setMfaMsg({ type: 'danger', text: err.response?.data?.error || t('error') }); }
    finally { setMfaLoading(false); }
  }

  return (
    <div className="page-body">
      <div className="page-header"><h2>{t('account_title')}</h2></div>

      <div style={{ maxWidth: 560 }}>
        {/* Accountgegevens */}
        <div className="card mb-3"><div className="card-body">
          <div className="mb-1"><span className="text-muted small">{t('user_name')}: </span><strong>{user?.name}</strong></div>
          <div className="mb-1"><span className="text-muted small">{t('user_email')}: </span>{user?.email}</div>
          <div><span className="text-muted small">{t('user_role')}: </span>{user?.role}</div>
        </div></div>

        {/* MFA */}
        <div className="card mb-3"><div className="card-body">
          <h6 className="mb-2">{t('account_mfa')}</h6>
          {mfaMsg && <div className={`alert alert-${mfaMsg.type} py-2`}>{mfaMsg.text}</div>}

          {user?.mfa_enabled ? (
            <>
              <div className="text-success small mb-2"><i className="bi bi-shield-check me-1" />{t('account_mfa_on')}</div>
              {!disabling ? (
                <button className="btn btn-outline-danger btn-sm"
                  onClick={() => { setDisabling(true); setDisableCode(''); setMfaMsg(null); }}>
                  {t('account_mfa_disable')}
                </button>
              ) : (
                <form onSubmit={disableMfa} className="d-flex gap-2 align-items-end flex-wrap">
                  <div>
                    <label className="form-label small mb-1">{t('mfa_code')}</label>
                    <input className="form-control form-control-sm" style={{ width: 120 }}
                      value={disableCode} onChange={e => setDisableCode(e.target.value)} maxLength={6} autoFocus />
                  </div>
                  <button className="btn btn-danger btn-sm" disabled={mfaLoading}>{t('account_mfa_disable')}</button>
                  <button type="button" className="btn btn-link btn-sm" onClick={() => setDisabling(false)}>{t('cancel')}</button>
                </form>
              )}
            </>
          ) : (
            <>
              <div className="text-muted small mb-2">{t('account_mfa_intro')}</div>
              {!setup ? (
                <button className="btn btn-primary btn-sm" onClick={startMfa} disabled={mfaLoading}>
                  {mfaLoading && <span className="spinner-border spinner-border-sm me-2" />}{t('account_mfa_enable')}
                </button>
              ) : (
                <div>
                  <div className="small mb-2">{t('account_mfa_scan')}</div>
                  <img src={setup.qr} alt="QR" style={{ width: 180, height: 180 }} className="mb-2 border rounded" />
                  <div className="small text-muted mb-2">{t('account_mfa_manual')}: <code>{setup.secret}</code></div>
                  <form onSubmit={confirmMfa} className="d-flex gap-2 align-items-end flex-wrap">
                    <div>
                      <label className="form-label small mb-1">{t('mfa_code')}</label>
                      <input className="form-control form-control-sm" style={{ width: 120 }}
                        value={mfaCode} onChange={e => setMfaCode(e.target.value)} maxLength={6} autoFocus />
                    </div>
                    <button className="btn btn-primary btn-sm" disabled={mfaLoading}>{t('account_mfa_confirm')}</button>
                  </form>
                </div>
              )}
            </>
          )}
        </div></div>

        {/* Wachtwoord wijzigen */}
        <div className="card"><div className="card-body">
          <h6 className="mb-2">{t('account_change_pw')}</h6>
          {pwMsg && <div className={`alert alert-${pwMsg.type} py-2`}>{pwMsg.text}</div>}
          <form onSubmit={changePassword}>
            <div className="mb-2">
              <label className="form-label small">{t('account_current_pw')}</label>
              <input type="password" className="form-control" value={pwForm.current}
                onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} required autoComplete="current-password" />
            </div>
            <div className="mb-2">
              <label className="form-label small">{t('reset_password')}</label>
              <input type="password" className="form-control" value={pwForm.next}
                onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} required minLength={8} autoComplete="new-password" />
            </div>
            <div className="mb-3">
              <label className="form-label small">{t('reset_password_confirm')}</label>
              <input type="password" className="form-control" value={pwForm.confirm}
                onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} required minLength={8} autoComplete="new-password" />
            </div>
            <button className="btn btn-primary btn-sm" disabled={pwLoading}>
              {pwLoading && <span className="spinner-border spinner-border-sm me-2" />}{t('save')}
            </button>
          </form>
        </div></div>
      </div>
    </div>
  );
}
